import React, { useState, useEffect } from "react";
import { useAxios } from "../hooks/axios";
import {
  Button,
  Table,
  Modal,
  TextInput,
  Group,
  Textarea,
  Box,
  createStyles,
  ScrollArea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";

interface Product {
  id: string;
  name: string;
  amount: number;
  description?: string;
  updatedAt: string;
  createdAt: string;
}

const useStyles = createStyles((theme) => ({
  container: {
    width: "100%",
    maxWidth: "830px",
    margin: "0 auto",
    padding: theme.spacing.md,
  },
  tableContainer: {
    overflowX: "auto",
  },
  responsiveTable: {
    minWidth: "100%",
    [theme.fn.smallerThan("sm")]: {
      "& th, & td": {
        padding: "8px 4px",
      },
    },
  },
  responsiveButton: {
    [theme.fn.smallerThan("xs")]: {
      padding: "4px 8px",
      fontSize: theme.fontSizes.xs,
    },
  },
  buttonGroup: {
    [theme.fn.smallerThan("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
      "& > *": {
        width: "100%",
        marginBottom: theme.spacing.xs,
      },
    },
  },
}));

const ProductList: React.FC = () => {
  const { classes } = useStyles();
  const [products, setProducts] = useState<Product[]>([]);
  const { request } = useAxios();
  const [modalOpened, setModalOpened] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [form, setForm] = useState<{
    id?: string;
    name: string;
    amount: string;
    description?: string;
  }>({
    name: "",
    amount: "0",
    description: "",
  });

  const fetchProducts = async () => {
    try {
      const response = await request({
        url: "/product/getAllProducts",
        method: "GET",
      });
      setProducts(response);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const openCreateModal = () => {
    setSelectedProduct(null);
    setForm({
      name: "",
      amount: "0",
      description: "",
    });
    setModalOpened(true);
  };

  const openEditModal = (product: Product) => {
    setSelectedProduct(product);
    setForm({
      id: product.id,
      name: product.name,
      amount: (product.amount/100).toString(),
      description: product.description || "",
    });
    setModalOpened(true);
  };

  const handleCloseModal = () => {
    setModalOpened(false);
    setSelectedProduct(null);
    setForm({
      name: "",
      amount: "0",
      description: "",
    });
  };

  const handleSave = async () => {
    try {
      const pin = localStorage.getItem("setup-pin");
      const amountNumber = parseFloat(form.amount);
       if (isNaN(amountNumber)) {
         notifications.show({
           title: "Error",
           message: "Amount must be a valid number",
           color: "red",
         });
         return;
       }
      const amountNumberInCents = Math.round(amountNumber * 100);

      if (selectedProduct) {
        // Update product
        await request({
          url: "/product/updateProduct",
          method: "POST",
          data: {
            pin,
            id: form.id,
            name: form.name,
            amount: amountNumberInCents,
            description: form.description,
          },
        });
        notifications.show({
          title: "Success",
          message: "Product updated successfully",
          color: "green",
        });
      } else {
        // Create product
        await request({
          url: "/product/createProduct",
          method: "POST",
          data: {
            pin,
            name: form.name,
            amount: amountNumberInCents,
            description: form.description,
          },
        });
        notifications.show({
          title: "Success",
          message: "Product created successfully",
          color: "green",
        });
      }
      handleCloseModal();
      fetchProducts();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error.response?.data?.message || "Operation failed",
        color: "red",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this product?")) {
      return;
    }
    try {
      const pin = localStorage.getItem("setup-pin");
      await request({
        url: "/product/deleteProduct",
        method: "POST",
        data: {
          pin,
          id,
        },
      });
      notifications.show({
        title: "Success",
        message: "Product deleted successfully",
        color: "green",
      });
      fetchProducts();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error.response?.data?.message || "Deletion failed",
        color: "red",
      });
    }
  };

  const formatDate = (date: string) => {
    const dateObj = new Date(date);
    return `${dateObj.toLocaleDateString()} ${dateObj.toLocaleTimeString()}`;
  };

  return (
    <Box className={classes.container}>
      <Button
        onClick={openCreateModal}
        mb="md"
        className={classes.responsiveButton}
      >
        Add Product
      </Button>

      <ScrollArea className={classes.tableContainer}>
        <Table className={classes.responsiveTable} highlightOnHover>
          <thead>
            <tr>
              <th>#</th>
              <th>Product Name</th>
              <th>Amount</th>
              <th>Description</th>
              <th>Actions</th>
              <th>Updated At</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product: Product, index: number) => (
              <tr key={product.id}>
                <td>{index + 1}</td>
                <td>{product.name}</td>
                <td>{(product.amount / 100).toFixed(2)}</td>
                <td>{product.description}</td>
                <td>
                  <Group spacing="xs" className={classes.buttonGroup}>
                    <Button
                      size="xs"
                      onClick={() => openEditModal(product)}
                      color="blue"
                      className={classes.responsiveButton}
                    >
                      Edit
                    </Button>
                    <Button
                      size="xs"
                      onClick={() => handleDelete(product.id)}
                      color="red"
                      className={classes.responsiveButton}
                    >
                      Delete
                    </Button>
                  </Group>
                </td>
                <td>{formatDate(product.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ScrollArea>

      <Modal
        opened={modalOpened}
        onClose={handleCloseModal}
        title={selectedProduct ? "Edit Product" : "Create Product"}
      >
        <TextInput
          label="Product Name"
          placeholder="Enter product name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
          mb="sm"
        />
        <TextInput
          label="Amount"
          placeholder="Enter amount"
          type="text"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })} // 先存字串
          required
          mb="sm"
        />
        <Textarea
          label="Description"
          placeholder="Enter description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          mb="sm"
        />
        <Group position="right" mt="md">
          <Button onClick={handleSave} className={classes.responsiveButton}>
            {selectedProduct ? "Update" : "Create"}
          </Button>
          <Button
            variant="outline"
            onClick={handleCloseModal}
            className={classes.responsiveButton}
          >
            Cancel
          </Button>
        </Group>
      </Modal>
    </Box>
  );
};

export default ProductList;
